import React from 'react';
import { observer } from 'mobx-react-lite';
import contactFormStore from '../../stores/ContactFormStore';
import LocationAndHours from './LocationAndHours';
import UnderwaterAccent from './UnderwaterAccent';

const ContactUs: React.FC = observer(() => {
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    contactFormStore.submitForm();
  };

  return (
    // 1) Outer container spans the entire screen
    <div className="relative w-screen min-h-screen">
      {/* Absolutely positioned background accent */}
      <UnderwaterAccent />

      {/* 3) Inner container for your contact form + content */}
      <div className="relative flex flex-col">
        {/* Full-screen Contact Section */}
        <div
          className="
            flex
            flex-col
            items-center
            text-center
            justify-center
            px-4
            pb-8
            space-y-12
            h-screen
            mt-[20vh] md:mt-0
          "
        >
          {/* Page Title */}
          <h1 className="text-6xl font-bold text-maroon font-charles">
            Contact Us
          </h1>

          {/* Intro Text */}
          <p className="max-w-lg text-sea mb-4 text-lg font-prosto leading-relaxed">
            We would love to hear from you! Whether you have a question,
            feedback, or want to book an event, feel free to reach out.
          </p>

          {/* Contact Form */}
          <form
            onSubmit={handleSubmit}
            className="flex flex-col space-y-6 w-full max-w-md"
          >
            {/* NAME */}
            <div className="flex items-center space-x-4">
              <label
                htmlFor="name"
                className="text-sm whitespace-nowrap text-sea font-prosto"
              >
                Name
              </label>
              <input
                id="name"
                type="text"
                value={contactFormStore.name}
                onChange={(e) =>
                  contactFormStore.setField('name', e.target.value)
                }
                className="
                  border-b border-sea
                  text-sea
                  font-prosto
                  bg-transparent
                  focus:outline-none
                  py-1
                  w-full
                "
                required
              />
            </div>

            {/* EMAIL */}
            <div className="flex items-center space-x-4">
              <label
                htmlFor="email"
                className="text-sm whitespace-nowrap text-sea font-prosto"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                value={contactFormStore.email}
                onChange={(e) =>
                  contactFormStore.setField('email', e.target.value)
                }
                className="
                  border-b border-sea
                  text-sea
                  font-prosto
                  bg-transparent
                  focus:outline-none
                  py-1
                  w-full
                "
                required
              />
            </div>

            {/* PHONE */}
            <div className="flex items-center space-x-4">
              <label
                htmlFor="phone"
                className="text-sm whitespace-nowrap text-sea font-prosto"
              >
                Phone
              </label>
              <input
                id="phone"
                type="text"
                value={contactFormStore.phone}
                onChange={(e) =>
                  contactFormStore.setField('phone', e.target.value)
                }
                className="
                  border-b border-sea
                  text-sea
                  font-prosto
                  bg-transparent
                  focus:outline-none
                  py-1
                  w-full
                "
              />
            </div>

            {/* SUBJECT */}
            <div className="flex items-center space-x-4">
              <label
                htmlFor="subject"
                className="text-sm whitespace-nowrap text-sea font-prosto"
              >
                Subject
              </label>
              <select
                id="subject"
                value={contactFormStore.subject}
                onChange={(e) =>
                  contactFormStore.setField('subject', e.target.value)
                }
                className="
                  border-b border-sea
                  text-sea
                  font-prosto
                  bg-transparent
                  focus:outline-none
                  py-1
                  w-full
                "
              >
                <option value="General Inquiry">General Inquiry</option>
                <option value="Event Booking">Event Booking</option>
                <option value="Feedback">Feedback</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* MESSAGE */}
            <div className="flex items-center space-x-4">
              <label
                htmlFor="message"
                className="text-sm whitespace-nowrap text-sea font-prosto"
              >
                Message
              </label>
              <textarea
                id="message"
                rows={5}
                value={contactFormStore.message}
                onChange={(e) =>
                  contactFormStore.setField('message', e.target.value)
                }
                className="
                  border-b border-sea
                  text-sea
                  font-prosto
                  bg-transparent
                  focus:outline-none
                  py-1
                  w-full
                  resize-none
                "
                required
              />
            </div>

            {/* SUBMIT BUTTON */}
            <button
              type="submit"
              disabled={contactFormStore.isSubmitting}
              className="
                w-1/2
                translate-x-1/2
                px-8
                py-4
                bg-sea
                text-white
                text-sm
                font-prosto
                rounded-full
                hover:bg-sea/80
                disabled:opacity-50
              "
            >
              {contactFormStore.isSubmitting ? 'Sending...' : 'Send Message'}
            </button>

            {/* Feedback Messages */}
            {contactFormStore.isSuccess === false &&
              contactFormStore.errorMessage && (
                <div className="text-red-600 text-sm mt-2">
                  {contactFormStore.errorMessage}
                </div>
              )}
            {contactFormStore.isSuccess === true && (
              <div className="text-green-600 text-sm mt-2">
                Thank you! We have received your message.
              </div>
            )}
          </form>

          {/* Additional Info Section */}
          <div className="flex flex-col items-center pt-6">
            <a
              href="tel:+18056795657"
              className="text-maroon font-charles text-xl lg:text-7xl"
            >
              (805) 679-5657
            </a>
            <a
              href="mailto:yonaredzsb@yahoo.com"
              className="text-sea font-prosto text-xs lg:text-lg"
            >
              YONAREDZSB@YAHOO.COM
            </a>
          </div>
        </div>

        {/* Location & Hours section (below) */}
        <LocationAndHours />
      </div>
    </div>
  );
});

export default ContactUs;
